/**
 * Carrinho JVenancio (sections/cart-drawer-hibisco.liquid) sobre o drawer Shine.
 *
 * - O Shine pede a secção `cart-drawer`; o id real vem de `data-cdh-section` (grupo aside).
 * - Produtos bônus da barra de prêmios: entram/saem conforme a meta (linhas com `_cdh_premio`).
 * - Kits fechados (`_kit_id` + `_kit_closed`): quantidade e remoção do grupo inteiro.
 * - Cálculo de frete por CEP, setas/autoplay do produto complementar e abertura no editor.
 */
(() => {
  if (window.__cartDrawerJVenancio) return;
  window.__cartDrawerJVenancio = true;

  const source = 'cart-drawer';
  const originalFetch = window.fetch.bind(window);
  const root = window.Shopify?.routes?.root || '/';
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mutationPattern = /\/cart\/(add|change|update)(\.js)?(\?|$)/;

  const host = () => document.getElementById('shtCartDrawer');
  const sectionId = () => host()?.getAttribute('data-cdh-section') || '';

  const rewrite = (value) => {
    const target = sectionId();
    const pattern = new RegExp(`(^|["',=])${source}(?!-)(?=["',&,]|$)`, 'g');
    return value.replace(pattern, `$1${target}`);
  };

  const aliasSection = (data) => {
    const target = sectionId();
    if (!data || typeof data !== 'object') return data;
    if (data.sections?.[target] && !data.sections[source]) data.sections[source] = data.sections[target];
    if (data[target] && !data[source]) data[source] = data[target];
    return data;
  };

  const requestUrl = (input) => (typeof input === 'string' ? input : input?.url || '');

  window.fetch = async (input, init) => {
    if (!sectionId()) return originalFetch(input, init);

    let url = input;
    let nextInit = init;
    let touched = false;

    if (typeof input === 'string' && input.includes(source)) {
      url = rewrite(input);
      touched = url !== input;
    }

    if (init?.body instanceof FormData) {
      const body = new FormData();
      let changed = false;

      for (const [key, value] of init.body.entries()) {
        if (typeof value === 'string' && value.includes(source)) {
          const rewritten = rewrite(value);
          if (rewritten !== value) changed = true;
          body.append(key, rewritten);
        } else {
          body.append(key, value);
        }
      }

      if (changed) {
        nextInit = { ...init, body };
        touched = true;
      }
    } else if (typeof init?.body === 'string' && init.body.includes(source)) {
      const body = rewrite(init.body);
      if (body !== init.body) {
        nextInit = { ...init, body };
        touched = true;
      }
    }

    const response = await originalFetch(url, nextInit);

    if (response.ok && mutationPattern.test(requestUrl(input))) schedulePrizeSync();
    if (!touched) return response;

    try {
      const data = aliasSection(await response.clone().json());
      return new Response(JSON.stringify(data), {
        status: response.status,
        statusText: response.statusText,
        headers: new Headers(response.headers)
      });
    } catch {
      return response;
    }
  };

  const updateHeaderCount = (count) => {
    if (count === null || count === '') return;

    for (const node of document.querySelectorAll('.cart-count-number')) node.textContent = count;

    const empty = count === '0';
    for (const bubble of document.querySelectorAll('.header__cart-count')) {
      bubble.classList.toggle('opacity-0', empty);
      bubble.classList.toggle('hidden-xs', empty);
    }
  };

  const replaceDrawer = (html) => {
    if (!html) return;

    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('.js-cart-drawer-wrapper');
    const current = document.querySelector('#cartDrawer .js-cart-drawer-wrapper');
    if (!next || !current) return;

    current.replaceWith(next);
    const drawer = host();
    if (drawer) drawer.drawerWrapperElement = next;
    updateHeaderCount(next.getAttribute('data-cart-count'));
  };

  const postJson = async (path, payload, withSections = true) => {
    const body = withSections
      ? { ...payload, sections: sectionId(), sections_url: window.location.pathname }
      : payload;
    const response = await originalFetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => null);
    return response.ok ? data : null;
  };

  const getCart = async () => {
    const response = await originalFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  };

  const mutate = async (path, payload) => {
    const data = await postJson(path, payload);
    replaceDrawer(data?.sections?.[sectionId()]);
    schedulePrizeSync();
    return data;
  };

  /* Produtos bônus da barra de prêmios */

  const prizeConfig = () => {
    const drawer = host();
    const variants = drawer?.getAttribute('data-cdh-prize-variants');
    if (!variants) return null;

    return {
      mode: drawer.getAttribute('data-cdh-prize-mode') || 'quantity',
      variants: variants.split(',').map(Number),
      thresholds: (drawer.getAttribute('data-cdh-prize-thresholds') || '').split(',').map(Number)
    };
  };

  let prizeTimer = 0;
  let prizeRunning = false;
  let prizeQueued = false;

  const syncPrizes = async () => {
    const config = prizeConfig();
    if (!config) return;

    if (prizeRunning) {
      prizeQueued = true;
      return;
    }
    prizeRunning = true;

    try {
      const cart = await getCart();
      const prizeLines = cart.items.filter((item) => item.properties?._cdh_premio);
      const value = cart.items
        .filter((item) => !item.properties?._cdh_premio)
        .reduce((sum, item) => sum + (config.mode === 'quantity' ? item.quantity : item.final_line_price), 0);
      const wanted = config.variants.filter((_, index) => value >= config.thresholds[index]);

      const updates = {};
      const kept = new Set();
      for (const line of prizeLines) {
        if (!wanted.includes(line.variant_id) || kept.has(line.variant_id)) {
          updates[line.key] = 0;
        } else {
          kept.add(line.variant_id);
          if (line.quantity !== 1) updates[line.key] = 1;
        }
      }
      const toAdd = wanted.filter((id) => !kept.has(id));

      if (!Object.keys(updates).length && !toAdd.length) return;

      let data = null;
      if (Object.keys(updates).length) data = await postJson('cart/update.js', { updates }, !toAdd.length);
      if (toAdd.length) {
        data = await postJson('cart/add.js', {
          items: toAdd.map((id) => ({ id, quantity: 1, properties: { _cdh_premio: '1' } }))
        });
      }
      replaceDrawer(data?.sections?.[sectionId()]);
    } catch {
      /* Sem rede ou produto bônus indisponível: mantém o carrinho como está. */
    } finally {
      prizeRunning = false;
      if (prizeQueued) {
        prizeQueued = false;
        schedulePrizeSync();
      }
    }
  };

  function schedulePrizeSync() {
    if (!prizeConfig()) return;
    clearTimeout(prizeTimer);
    prizeTimer = setTimeout(syncPrizes, 400);
  }

  /* Kits fechados */

  const updateKit = async (kitId, quantity) => {
    const cart = await getCart();
    const updates = {};
    for (const item of cart.items) {
      if (item.properties?._kit_id === kitId) updates[item.key] = quantity;
    }
    if (Object.keys(updates).length) await mutate('cart/update.js', { updates });
  };

  /* Produto complementar */

  const addVariant = async (button) => {
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const data = await mutate('cart/add.js', {
      items: [{ id: Number(button.getAttribute('data-cdh-add')), quantity: Number(button.getAttribute('data-cdh-qty')) || 1 }]
    });

    if (!data) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  };

  const slide = (track, direction) => {
    const item = track.querySelector('.cdh-comp__item');
    if (!item) return;

    const step = item.offsetWidth + (parseFloat(getComputedStyle(track).columnGap) || 0);
    const max = track.scrollWidth - track.clientWidth - 2;
    const behavior = motion.matches ? 'auto' : 'smooth';

    if (direction > 0 && track.scrollLeft >= max) {
      track.scrollTo({ left: 0, behavior });
    } else if (direction < 0 && track.scrollLeft <= 2) {
      track.scrollTo({ left: track.scrollWidth, behavior });
    } else {
      track.scrollBy({ left: step * direction, behavior });
    }
  };

  let autoplayTimer = 0;

  const stopAutoplay = () => {
    clearInterval(autoplayTimer);
    autoplayTimer = 0;
  };

  const startAutoplay = () => {
    stopAutoplay();
    if (motion.matches) return;

    const block = host()?.querySelector('[data-cdh-autoplay]');
    if (!block) return;

    autoplayTimer = setInterval(() => {
      const current = host()?.querySelector('[data-cdh-autoplay]');
      if (!current || !host()?.classList.contains('active')) return stopAutoplay();
      if (current.matches(':hover, :focus-within')) return;
      const track = current.querySelector('[data-cdh-comp-track]');
      if (track) slide(track, 1);
    }, Number(block.getAttribute('data-cdh-autoplay')) || 5000);
  };

  /* Cálculo de frete */

  const formatMoney = (value) => {
    const amount = Number(String(value ?? 0).replace(',', '.'));
    if (!Number.isFinite(amount)) return String(value);
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount);
  };

  const businessDays = (days) => (days === 1 ? '1 dia útil' : `${days} dias úteis`);

  const deliveryTime = (rate) => {
    const range = rate.delivery_range ? String(rate.delivery_range).trim() : '';

    if (range && range !== 'null') {
      if (range.includes(',') && range.includes('-')) {
        const [start, end] = range.split(',').map((part) => part.trim());
        const today = Date.now();
        const from = Math.ceil((new Date(start) - today) / 86400000);
        const to = Math.ceil((new Date(end) - today) / 86400000);
        if (Number.isNaN(from)) return range;
        if (from <= 0) return 'Entrega hoje';
        if (start === end) return businessDays(from);
        return `${from} a ${to} dias úteis`;
      }
      return range;
    }

    if (rate.delivery_date && rate.delivery_date !== 'null') return String(rate.delivery_date);

    const days = parseInt(rate.delivery_estimate_business_days || rate.delivery_estimate_transit_time_business_days, 10);
    if (days > 0) return businessDays(days);

    if (Array.isArray(rate.delivery_days) && rate.delivery_days.length) {
      return businessDays(rate.delivery_days[rate.delivery_days.length - 1]);
    }

    return '';
  };

  const showShippingMessage = (result, type, text) => {
    result.replaceChildren();
    const alert = document.createElement('p');
    alert.className = `cdh__shipping-alert cdh__shipping-alert--${type}`;
    alert.textContent = text;
    result.append(alert);
    result.hidden = false;
  };

  const renderRates = (result, rates) => {
    result.replaceChildren();

    for (const rate of rates) {
      const option = document.createElement('div');
      option.className = 'cdh__shipping-option';

      const row = document.createElement('div');
      row.className = 'cdh__shipping-row';

      const name = document.createElement('span');
      name.textContent = rate.name || '';

      const price = document.createElement('span');
      price.className = 'cdh__shipping-price';
      if (Number(rate.price) === 0) {
        price.classList.add('cdh__shipping-price--free');
        price.textContent = 'GRÁTIS';
      } else {
        price.textContent = formatMoney(rate.price);
      }

      row.append(name, price);
      option.append(row);

      const time = deliveryTime(rate);
      if (time) {
        const delivery = document.createElement('span');
        delivery.className = 'cdh__shipping-time';
        delivery.textContent = time;
        option.append(delivery);
      }

      result.append(option);
    }

    result.hidden = false;
  };

  const readRates = async (response) => {
    if (!response.ok || !(response.headers.get('Content-Type') || '').includes('application/json')) return null;
    const data = await response.json();
    return Array.isArray(data?.shipping_rates) ? data.shipping_rates : null;
  };

  const fetchRates = async (zip, province) => {
    const params = new URLSearchParams({
      'shipping_address[zip]': zip,
      'shipping_address[country]': 'BR',
      'shipping_address[province]': province
    });
    const headers = { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' };

    const direct = await readRates(await originalFetch(`${root}cart/shipping_rates.json?${params}`, { headers })).catch(() => null);
    if (direct?.length) return direct;

    const prepared = await originalFetch(`${root}cart/prepare_shipping_rates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Requested-With': 'XMLHttpRequest' },
      body: params
    }).catch(() => null);
    if (!prepared?.ok) return direct;

    for (let attempt = 0; attempt < 15; attempt += 1) {
      await new Promise((resolve) => setTimeout(resolve, 800));
      const response = await originalFetch(`${root}cart/async_shipping_rates`, { headers }).catch(() => null);
      if (response?.status !== 200) continue;
      const rates = await readRates(response).catch(() => null);
      if (rates) return rates;
    }

    return null;
  };

  const calculateShipping = async (button) => {
    const wrapper = button.closest('[data-cdh-shipping]');
    const input = wrapper?.querySelector('[data-cdh-shipping-cep]');
    const result = wrapper?.querySelector('[data-cdh-shipping-result]');
    if (!input || !result || button.disabled) return;

    const zip = input.value.replace(/\D/g, '');
    if (zip.length !== 8) {
      showShippingMessage(result, 'warning', 'Digite um CEP válido (8 dígitos).');
      return;
    }

    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Calculando...';
    result.hidden = true;

    let province = 'BR';
    try {
      const response = await originalFetch(`https://brasilapi.com.br/api/cep/v1/${zip}`);
      if (response.ok) province = (await response.json())?.state || province;
    } catch {
      /* Sem UF: a Shopify ainda tenta calcular só com o CEP. */
    }

    const rates = await fetchRates(zip, province).catch(() => null);

    if (rates?.length) {
      renderRates(result, rates);
    } else if (rates) {
      showShippingMessage(result, 'warning', 'Não foi possível encontrar opções de frete para o CEP informado.');
    } else {
      showShippingMessage(result, 'danger', 'Não foi possível calcular o frete neste momento. Verifique o CEP ou tente novamente mais tarde.');
    }

    button.textContent = label;
    button.disabled = false;
  };

  /* Eventos (delegados: o conteúdo do drawer é substituído a cada atualização) */

  document.addEventListener('click', (event) => {
    if (!event.target.closest('#shtCartDrawer[data-cdh-section]')) return;

    const addButton = event.target.closest('[data-cdh-add]');
    if (addButton) {
      event.preventDefault();
      addVariant(addButton);
      return;
    }

    const nav = event.target.closest('[data-cdh-comp-nav]');
    if (nav) {
      const track = nav.closest('[data-cdh-comp]')?.querySelector('[data-cdh-comp-track]');
      if (track) slide(track, Number(nav.getAttribute('data-cdh-comp-nav')));
      return;
    }

    const kitStep = event.target.closest('[data-cdh-kit-step]');
    if (kitStep) {
      const kit = kitStep.closest('[data-cdh-kit]');
      const current = parseInt(kit?.querySelector('.cdh__qty-value')?.textContent, 10) || 1;
      const next = Math.max(0, current + Number(kitStep.getAttribute('data-cdh-kit-step')));
      kit?.querySelectorAll('button').forEach((button) => (button.disabled = true));
      updateKit(kit.getAttribute('data-cdh-kit'), next);
      return;
    }

    const kitRemove = event.target.closest('[data-cdh-kit-remove]');
    if (kitRemove) {
      kitRemove.disabled = true;
      updateKit(kitRemove.getAttribute('data-cdh-kit-remove'), 0);
      return;
    }

    const shippingButton = event.target.closest('[data-cdh-shipping-btn]');
    if (shippingButton) calculateShipping(shippingButton);
  });

  document.addEventListener('input', (event) => {
    const input = event.target.closest?.('[data-cdh-shipping-cep]');
    if (!input) return;
    const digits = input.value.replace(/\D/g, '').slice(0, 8);
    input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const input = event.target.closest?.('[data-cdh-shipping-cep]');
    if (!input) return;
    event.preventDefault();
    const button = input.closest('[data-cdh-shipping]')?.querySelector('[data-cdh-shipping-btn]');
    if (button) calculateShipping(button);
  });

  const drawer = host();
  if (drawer?.hasAttribute('data-cdh-section')) {
    new MutationObserver(() => {
      if (drawer.classList.contains('active')) {
        if (!autoplayTimer) startAutoplay();
      } else {
        stopAutoplay();
      }
    }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  /* Theme Editor */

  if (window.Shopify?.designMode) {
    const inDrawer = (target) => Boolean(target?.querySelector?.('#shtCartDrawer[data-cdh-section]'));
    const open = () => window.shtCartDrawer?.openDrawer?.();

    document.addEventListener('shopify:section:select', (event) => inDrawer(event.target) && open());
    document.addEventListener('shopify:block:select', (event) => inDrawer(event.target) && open());
    document.addEventListener('shopify:section:deselect', (event) => {
      if (inDrawer(event.target)) window.shtCartDrawer?.closeDrawer?.();
    });
  }

  if (prizeConfig() && host()?.querySelector('[data-cart-count]')?.getAttribute('data-cart-count') !== '0') {
    syncPrizes();
  }
})();
